import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Spinner from '@/components/Spinner';
import ProductCard from '@/components/ProductCard';
import { API } from '@/lib/utils';
import { useSEO } from '@/lib/useSEO';
import { ArrowLeft, Clock, Tag } from '@phosphor-icons/react';

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useSEO({
    title: post?.title,
    description: post?.excerpt,
    image: post?.hero_image,
    path: `/gids/${slug}`,
    type: 'article',
    jsonLd: post ? {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: post.title,
      description: post.excerpt,
      image: post.hero_image,
      datePublished: post.published_date,
      author: { '@type': 'Organization', name: 'Noodpakket.nl' },
      publisher: {
        '@type': 'Organization',
        name: 'Noodpakket.nl',
        logo: { '@type': 'ImageObject', url: 'https://noodpakket.nl/logo.png' },
      },
      mainEntityOfPage: `https://noodpakket.nl/gids/${slug}`,
    } : null,
  });

  useEffect(() => {
    setPost(null); setNotFound(false); setRelated([]);
    axios.get(`${API}/blog/${slug}`)
      .then(async (r) => {
        setPost(r.data);
        const slugs = r.data.related_products || [];
        if (slugs.length > 0) {
          const products = await Promise.all(
            slugs.map((s) => axios.get(`${API}/products/${s}`).then((res) => res.data).catch(() => null))
          );
          setRelated(products.filter(Boolean));
        }
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="container py-24 text-center">
      <h1 className="font-display font-black text-3xl mb-4">Artikel niet gevonden</h1>
      <Link to="/gids" className="text-primary font-semibold">Terug naar de gids</Link>
    </div>
  );
  if (!post) return <Spinner />;

  return (
    <article data-testid={`page-blog-${slug}`}>
      <header className="border-b border-border bg-card">
        <div className="container py-12 lg:py-16 max-w-3xl">
          <Link to="/gids" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary mb-6">
            <ArrowLeft size={16} /> Terug naar gids
          </Link>
          <div className="flex items-center gap-3 text-xs text-muted-foreground mb-5">
            <span className="bg-primary/10 text-primary uppercase tracking-widest font-bold px-2 py-1 rounded-sm flex items-center gap-1">
              <Tag size={11} weight="bold" /> {post.category}
            </span>
            <span>{new Date(post.published_date).toLocaleDateString('nl-NL', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
            <span className="flex items-center gap-1"><Clock size={12} /> {post.reading_time_min} min lezen</span>
          </div>
          <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight">
            {post.title}
          </h1>
          <p className="mt-6 text-lg text-muted-foreground leading-relaxed">{post.excerpt}</p>
        </div>
      </header>

      <div className="container py-10 max-w-3xl">
        <div className="aspect-[16/9] bg-muted rounded-sm overflow-hidden border border-border mb-12">
          <img src={post.hero_image} alt={post.title} className="w-full h-full object-cover" />
        </div>

        <div className="prose-content text-foreground" data-testid="blog-body">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h2: ({ node, ...props }) => <h2 className="font-display font-black text-2xl lg:text-3xl mt-12 mb-4 leading-tight" {...props} />,
              h3: ({ node, ...props }) => <h3 className="font-display font-bold text-xl mt-8 mb-3" {...props} />,
              p: ({ node, ...props }) => <p className="text-base sm:text-lg leading-relaxed mb-5" {...props} />,
              ul: ({ node, ...props }) => <ul className="list-disc pl-6 mb-5 space-y-2 text-base sm:text-lg leading-relaxed" {...props} />,
              ol: ({ node, ...props }) => <ol className="list-decimal pl-6 mb-5 space-y-2 text-base sm:text-lg leading-relaxed" {...props} />,
              blockquote: ({ node, ...props }) => <blockquote className="border-l-4 border-accent bg-accent/5 px-6 py-4 my-6 italic" {...props} />,
              a: ({ node, href, ...props }) => {
                const isInternal = href?.startsWith('/');
                if (isInternal) return <Link to={href} className="text-primary underline underline-offset-2 hover:text-primary-hover font-medium" {...props} />;
                return <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2 hover:text-primary-hover font-medium" {...props} />;
              },
              table: ({ node, ...props }) => <div className="overflow-x-auto my-8"><table className="w-full border-collapse border border-border" {...props} /></div>,
              th: ({ node, ...props }) => <th className="border border-border bg-muted px-4 py-2 text-left font-bold" {...props} />,
              td: ({ node, ...props }) => <td className="border border-border px-4 py-2" {...props} />,
              code: ({ node, ...props }) => <code className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono" {...props} />,
              hr: () => <hr className="border-t border-border my-10" />,
              strong: ({ node, ...props }) => <strong className="font-bold text-foreground" {...props} />,
            }}
          >
            {post.body}
          </ReactMarkdown>
        </div>
      </div>

      {related.length > 0 && (
        <section className="bg-muted/50 border-t border-border mt-16">
          <div className="container py-16 lg:py-20">
            <div className="overline text-muted-foreground mb-3 flex items-center gap-2">
              <span className="w-8 h-0.5 bg-accent" /> Genoemde producten
            </div>
            <h2 className="font-display font-black text-2xl lg:text-3xl mb-10">In dit artikel besproken</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              {(related || []).map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}
